import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import FloatingButton from '../ui/FloatingButton';
import ConfirmModal from '../ui/ConfirmModal';
import StatusBadge from '../ui/StatusBadge';
import MessagesTab from './tabs/MessagesTab';
import LeagueTeamsTab from './tabs/LeagueTeamsTab';
import LeagueScheduleTab from './tabs/LeagueScheduleTab';
import LeagueStandingsTab from './tabs/LeagueStandingsTab';
import LeagueRulesCard from './LeagueRulesCard';
import LeagueFeesCard from './LeagueFeesCard';
import LeagueMatchesTab from './tabs/LeagueMatchesTab';
import { useLeagueDetails } from './hooks/useLeagueDetails';
import { apiClient } from '../../services/api/apiService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { eid } from '../../utils/entityId';
import { isCreatorOf } from '../../utils/rbac';
import { formatDateOnly } from '../../utils/dateOnly';
import { getLeagueFormat } from './leagueFormat';
import { NEXT_STEP, isFinalStatus, leagueStatusLabel, normalizeLeagueStatus } from './leagueStatus';
import { leagueScreenPath } from '../screen/screenUtils';
import { isTvScreenEnabled } from '../../config/features';

/**
 * LeagueDetails Component
 * Container component for displaying league details with tabbed content
 */
const LeagueDetails = ({ 
  league: leagueProp, 
  token, 
  user, 
  initialTab = 'overview',
  canManageLeagues, 
  showFeedback, 
  feedback, 
  clearFeedback, 
  formatDate, 
  formatTime, 
  openConfirm, 
  onBack 
}) => {
  const navigate = useNavigate();
  const { addEventListener } = useWebSocket();
  const [activeTab, setActiveTab] = useState(initialTab);
  // A link to another tab (?tab=messages) while the league is open switches to it
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [league, setLeague] = useState(leagueProp);
  const [statusChanging, setStatusChanging] = useState(false);
  const [closeRegConfirm, setCloseRegConfirm] = useState(false);
  const { 
    teams, 
    schedule, 
    standings, 
    standingsRules,
    leagueMatches, 
    scheduleMeta,
    matchesMeta,
    loadingMore,
    loading, 
    fetchLeagueTeams, 
    fetchLeagueSchedule, 
    fetchLeagueStandings, 
    fetchLeagueMatches 
  } = useLeagueDetails(league?.id);

  // Keep local league state in sync if parent passes an updated prop
  useEffect(() => {
    setLeague(leagueProp);
  }, [leagueProp]);

  // Listen for socket notification when this user's team is removed from a league
  useEffect(() => {
    if (!addEventListener) return;
    const handler = (data) => {
      // Only show if this is the league currently being viewed
      if (data.leagueId && league?.id && data.leagueId !== league.id) return;
      const msg = data.message || `${data.teamName || 'Your team'} has been removed from ${data.leagueName || 'the league'}.`;
      handleFeedback('warning', `🚨 ${msg}`);
    };
    return addEventListener('team_removed_from_league', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEventListener, league?.id]);

  // Fetch data when tab changes
  useEffect(() => {
    if (league?.id) {
      if (activeTab === 'teams') fetchLeagueTeams();
      else if (activeTab === 'schedule') fetchLeagueSchedule();
      else if (activeTab === 'standings') fetchLeagueStandings();
      else if (activeTab === 'matches') fetchLeagueMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, league?.id]);

  // Host-only actions (teams tab) use isHost; canManageLeagues comes from
  // Leagues.js and is true only for this league's creator or a super/platform
  // admin, matching the backend (BUG-31).
  const isHost = isCreatorOf(user, league);
  const canChangeStatus = isHost || canManageLeagues;
  // Venue screen (TV view): only when switched on; for the organizers, and for
  // anyone when the league is public
  const canOpenScreen = isTvScreenEnabled() && (canChangeStatus || league?.is_public !== false);

  const openScreen = () => {
    window.open(leagueScreenPath(league), '_blank', 'noopener');
    showFeedback('success', 'TV screen opened in a new tab. Press F11 there for full screen.');
  };

  // One lifecycle for every screen (leagueStatus.js); "active" reads as in progress
  const status = normalizeLeagueStatus(league?.status);
  const STATUS_TRANSITIONS = NEXT_STEP;

  const handleStatusChange = async (newStatus) => {
    setStatusChanging(true);
    try {
      let res;
      if (newStatus === 'cancelled') {
        // Cascade: cancel all non-completed matches too
        res = await apiClient.post(`/leagues/${league.id}/cancel`);
      } else {
        // Status changes follow the lifecycle on the server (LG-B10)
        res = await apiClient.post(`/leagues/${league.id}/status`, { status: newStatus });
      }
      const updated = res.data?.data?.league || res.data?.data || { ...league, status: newStatus };
      setLeague((prev) => ({ ...prev, ...updated }));
      showFeedback('success', `League is now ${leagueStatusLabel(newStatus)}`);
    } catch (err) {
      showFeedback('error', err.response?.data?.message || 'Failed to update league status');
    } finally {
      setStatusChanging(false);
    }
  };

  const formatStatus = (s) =>
    s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

  // Weekday names ("Saturdays") or the exact dates of a specific-dates league
  const formatGameDays = (l) => {
    const days = Array.isArray(l.game_days) && l.game_days.length ? l.game_days : (l.game_day ? [l.game_day] : []);
    if (!days.length) return '—';
    return days.map((d) => (/^\d{4}-\d{2}-\d{2}/.test(d) ? formatDate(d) : `${d}s`)).join(', ');
  };

  const handleFeedback = (type, message) => {
    showFeedback(type, message);
  };

  return (
    <GameLayout token={token} onLogout={() => {}}>
      <div className="leagues-gaming-layout">
        {/* Header */}
        <div className="leagues-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="league-details-title">🏆 {league.name}</h1>
                <p className="tournaments-subtitle-gaming">
                  {league.sport_type}
                  {league.season ? ` · ${league.season}` : ''}
                  {league.playoff_format ? ` · ${league.playoff_format.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}` : ''}
                </p>
                <StatusBadge status={status} className="league-details-status" />
              </div>

              <div className="header-actions-gaming">
                {/* Host status transition buttons */}
                {canChangeStatus && STATUS_TRANSITIONS[status] && (
                  <FloatingButton
                    variant={STATUS_TRANSITIONS[status].variant}
                    size="medium"
                    onClick={() => {
                      const next = STATUS_TRANSITIONS[status].nextStatus;
                      // "Close Registration" needs a confirmation warning
                      if (next === 'registration_closed') {
                        setCloseRegConfirm(true);
                      } else {
                        handleStatusChange(next);
                      }
                    }}
                    disabled={statusChanging}
                    icon={STATUS_TRANSITIONS[status].icon}
                    className="delay-2"
                    aria-label={STATUS_TRANSITIONS[status].label}
                    title={STATUS_TRANSITIONS[status].label}
                  >
                    <span className="btn-text">
                      {statusChanging ? 'Updating…' : STATUS_TRANSITIONS[status].label}
                    </span>
                  </FloatingButton>
                )}
                {canChangeStatus && !isFinalStatus(status) && (
                  <FloatingButton
                    variant="warning"
                    size="medium"
                    onClick={() => handleStatusChange('cancelled')}
                    disabled={statusChanging}
                    icon="⛔"
                    className="delay-3"
                    aria-label="Cancel league"
                    title="Cancel league"
                  >
                    <span className="btn-text">Cancel League</span>
                  </FloatingButton>
                )}
                {canOpenScreen && (
                  <FloatingButton
                    variant="secondary"
                    size="medium"
                    onClick={openScreen}
                    icon="📺"
                    className="delay-4"
                    aria-label="Open TV screen"
                    title="Live scores and standings for a TV at the venue. Opens in a new tab; press F11 for full screen."
                  >
                    <span className="btn-text">Open TV screen</span>
                  </FloatingButton>
                )}
                <FloatingButton
                  variant="outline"
                  size="medium"
                  onClick={onBack}
                  icon="←"
                  className="delay-4"
                  aria-label="Back to leagues"
                  title="Back to leagues"
                >
                  <span className="btn-text">Back</span>
                </FloatingButton>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Feedback Messages */}
        {feedback && (
          <div className={`league-feedback ${feedback.type}`}>
            <span>{feedback.message}</span>
            <button
              type="button"
              className="league-feedback-close"
              onClick={clearFeedback}
              aria-label="Dismiss message"
            >
              ×
            </button>
          </div>
        )}

        {/* Tab Navigation + Content */}
        <FloatingCard variant="primary" size="large" className="delay-3 league-tabs-card">
          {/* Pill-style tabs — same style as tournament page */}
          <div style={{ display: 'flex', gap: '0.25rem', padding: '0.25rem', background: 'rgba(0,0,0,0.25)', borderRadius: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {[
              { key: 'overview',  label: '📋 Overview' },
              { key: 'teams',     label: `🏅 ${getLeagueFormat(league).unitPlural} (${teams.length || 0})` },
              { key: 'schedule',  label: `📅 Schedule (${scheduleMeta?.counts?.to_play ?? schedule.length ?? 0})`,
                title: `${scheduleMeta?.counts?.to_play ?? schedule.length ?? 0} games to play${scheduleMeta?.counts?.awaiting_result ? `, ${scheduleMeta.counts.awaiting_result} awaiting a result` : ''}` },
              { key: 'matches',   label: `🎮 Matches (${matchesMeta?.counts?.total ?? leagueMatches.length ?? 0})` },
              { key: 'standings', label: '🏆 Standings' },
              { key: 'messages',  label: '💬 Messages' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                {...(tab.title ? { title: tab.title } : {})}
                style={{
                  flex: 1,
                  minWidth: '110px',
                  whiteSpace: 'nowrap',
                  padding: '0.65rem 0.9rem',
                  borderRadius: '0.55rem',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.key ? 700 : 500,
                  fontSize: '0.85rem',
                  transition: 'all 0.15s',
                  background: activeTab === tab.key ? 'rgba(99,102,241,0.35)' : 'transparent',
                  color: activeTab === tab.key ? '#e2e8f0' : '#94a3b8',
                  boxShadow: activeTab === tab.key ? '0 0 0 1px rgba(99,102,241,0.5)' : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="league-tab-content">
            {loading && activeTab !== 'messages' && activeTab !== 'overview' && (
              <div className="loading-state-gaming">
                <div className="loading-spinner-gaming"></div>
                <p>Loading {activeTab}...</p>
              </div>
            )}

            {activeTab === 'overview' && (
              <div className="league-overview-tab">
                <FloatingCard variant="secondary" size="large" className="delay-1">
                  <h3 className="form-section-title" style={{ marginBottom: '1.2rem' }}>📋 League Information</h3>
                  <div className="tournament-details-gaming">
                    {[
                      ['🏅', 'Sport',           league.sport_type || '—'],
                      ['📁', 'Category',         league.sport_category || '—'],
                      ['📅', 'Game Days',        formatGameDays(league)],
                      ['⏰', 'Game Time',        league.start_time && league.end_time ? `${formatTime(league.start_time)} – ${formatTime(league.end_time)}` : '—'],
                      ['📍', 'Venue',            league.venue_name || 'TBD'],
                      ['🏟️', 'Courts',           league.number_of_courts != null ? `${league.number_of_courts} court${league.number_of_courts !== 1 ? 's' : ''}` : '—'],
                      ...(!getLeagueFormat(league).isTeam ? [['🏸', 'Format', getLeagueFormat(league).isSingles ? 'Singles (players join individually)' : 'Doubles (players join as pairs)']] : []),
                      ['👥', getLeagueFormat(league).unitPlural, `${teams.length || 0} / ${league.total_teams || '?'} enrolled`],
                      ['📆', 'Season Start',     league.season_start_date ? formatDate(league.season_start_date) : '—'],
                      ['🏁', 'Season End',       league.season_end_date ? formatDate(league.season_end_date) : '—'],
                      ...(league.registration_deadline ? [['🔒', 'Reg. Deadline', formatDate(league.registration_deadline)]] : []),
                      ...(league.season ? [['🗓️', 'Season',          league.season]] : []),
                      ...(league.playoff_format ? [['⚔️', 'Playoff Format',  formatStatus(league.playoff_format)]] : []),
                      ...(league.games_per_team_per_day != null ? [['🎮', 'Games / Team / Day', String(league.games_per_team_per_day)]] : []),
                      ['🌐', 'Visibility',      league.is_public !== false ? 'Public' : 'Private (invite only)'],
                      ...(league.skill_level ? [['🏅', 'Skill Level', league.skill_level.charAt(0).toUpperCase() + league.skill_level.slice(1)]] : []),
                    ].map(([icon, label, value]) => (
                      <div key={label} className="detail-item">
                        <span className="detail-icon">{icon}</span>
                        <span className="detail-label">{label}</span>
                        <span className="detail-value">{value}</span>
                      </div>
                    ))}
                    {league.description && (
                      <div className="detail-item full-width">
                        <span className="detail-icon">📝</span>
                        <span className="detail-label">Description</span>
                        <p className="detail-description">{league.description}</p>
                      </div>
                    )}
                    {league.game_rules?.match_rules && (
                      <div className="detail-item full-width">
                        <span className="detail-icon">📜</span>
                        <span className="detail-label">Match Rules</span>
                        <p className="detail-description">{league.game_rules.match_rules}</p>
                      </div>
                    )}
                    {league.game_rules?.additional_notes && (
                      <div className="detail-item full-width">
                        <span className="detail-icon">🗒️</span>
                        <span className="detail-label">Notes</span>
                        <p className="detail-description">{league.game_rules.additional_notes}</p>
                      </div>
                    )}
                  </div>
                </FloatingCard>
                {league.fee_mode && league.fee_mode !== 'none' && (
                  <FloatingCard variant="secondary" size="large" className="delay-2">
                    <LeagueFeesCard league={league} />
                  </FloatingCard>
                )}
                <FloatingCard variant="secondary" size="large" className="delay-2">
                  <LeagueRulesCard
                    league={league}
                    onSaved={(rules) => {
                      setLeague((prev) => ({
                        ...prev,
                        number_of_courts: rules.courts.length,
                        game_rules: { ...(prev?.game_rules || {}), courts: rules.courts },
                      }));
                      fetchLeagueStandings();
                    }}
                  />
                </FloatingCard>
              </div>
            )}

            {!loading && activeTab === 'teams' && (
              <LeagueTeamsTab
                league={league}
                teams={teams}
                user={user}
                canManageLeagues={isHost}
                showFeedback={handleFeedback}
                openConfirm={openConfirm}
                fetchLeagueTeams={fetchLeagueTeams}
                loading={loading}
              />
            )}

            {!loading && activeTab === 'schedule' && (
              <LeagueScheduleTab
                league={league}
                schedule={schedule}
                teams={teams}
                canManageLeagues={canManageLeagues}
                showFeedback={handleFeedback}
                openConfirm={openConfirm}
                fetchLeagueSchedule={fetchLeagueSchedule}
                fetchLeagueMatches={fetchLeagueMatches}
                formatDate={formatDate}
                formatTime={formatTime}
                loading={loading}
                onFeedback={handleFeedback}
                onLeagueChanged={(changes) => setLeague((prev) => ({ ...prev, ...changes }))}
                meta={scheduleMeta}
                loadingMore={loadingMore}
                navigate={navigate}
              />
            )}

            {!loading && activeTab === 'matches' && (
              <LeagueMatchesTab
                leagueMatches={leagueMatches}
                meta={matchesMeta}
                onViewChange={(view) => fetchLeagueMatches({ view })}
                onLoadMore={() => fetchLeagueMatches({ append: true })}
                loadingMore={loadingMore}
                loading={loading}
                navigate={navigate}
                leagueId={eid(league) || league?.id}
              />
            )}

            {!loading && activeTab === 'standings' && (
              <LeagueStandingsTab
                standings={standings}
                rules={standingsRules}
                loading={loading}
                league={league}
              />
            )}

            {activeTab === 'messages' && (
              <MessagesTab 
                leagueId={league.id} 
                league={league}
                token={token} 
                currentUser={user} 
                canEmailAll={canChangeStatus}
              />
            )}
          </div>
        </FloatingCard>
      </div>

      {/* Close-registration confirmation */}
      <ConfirmModal
        isOpen={closeRegConfirm}
        title="🔒 Close Registration?"
        message={
          `Once registration is closed, no new teams can join and existing invitations will be void.\n\n` +
          (league.season_start_date
            ? `Your league is scheduled to start on ${formatDateOnly(league.season_start_date, { month: 'long', day: 'numeric', year: 'numeric' })}. ` +
              `It will automatically move to "In Progress" on that date.`
            : `Set a season start date so the league can automatically start when the time comes.`)
        }
        confirmText="Close Registration"
        cancelText="Keep Open"
        isDanger={false}
        onConfirm={() => { setCloseRegConfirm(false); handleStatusChange('registration_closed'); }}
        onCancel={() => setCloseRegConfirm(false)}
      />
    </GameLayout>
  );
};

export default LeagueDetails;
